import type { ChainId, ChainLayer } from '@cronos-app/sdk';
import { utils } from '@cronos-app/sdk';
import {
  currentWallet,
  getAddEthereumChainParameter,
  useDesiredChain,
  useDesiredNetwork,
} from '@cronos-app/wallet';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { toaster } from '@ui/components/Toaster';

import { useGlobalChainLayer } from './useGlobalChainLayer';

const switchNetwork = async (chainId: ChainId, isConnected: boolean) => {
  const networkConfig = getAddEthereumChainParameter(chainId);
  if (!isConnected) return;

  try {
    await currentWallet.connect(networkConfig);
  } catch (e: unknown) {
    toaster.create({
      type: 'error',
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      description: (e as Error).message || 'Failed to switch network',
    });
    throw e;
  }
};

export const useSwitchNetwork = (options: UseMutationOptions = {}) => {
  const chainLayer = useGlobalChainLayer();
  const desiredChainId = useDesiredChain(chainLayer);
  const isConnected = currentWallet.useIsConnected();

  return useMutation(async () => {
    await switchNetwork(desiredChainId, isConnected);
  }, options);
};

export const useSwitchChainLayer = (
  options: UseMutationOptions<void, unknown, { chainLayer: ChainLayer }> = {},
) => {
  const network = useDesiredNetwork();
  const isConnected = currentWallet.useIsConnected();

  return useMutation<void, unknown, { chainLayer: ChainLayer }>(
    async ({ chainLayer }) => {
      const chainId = utils.getChainOfLayer(chainLayer, network);
      await switchNetwork(chainId, isConnected);
    },
    options,
  );
};
